<template>
  <div class="visitor-checkin">
    <!-- Background Image with blur & transparency -->
    <div class="background-image"></div>

    <b-container fluid>
      <!-- Header -->
      <!-- <b-row>
        <b-col>
          <div class="page-header">
            <h3 class="page-title">
              <b-icon icon="person-check" class="title-icon" />
              Visitor Check-In
            </h3>
            <p class="page-subtitle">Verify visitors with face recognition</p>
          </div>
        </b-col>
      </b-row> -->

      <b-row>
        <!-- Left: Form -->
        <b-col lg="6" md="12">
          <b-card class="form-card glass-card" no-body>
            <template #header>
              <h5 class="card-header-title">
                <b-icon icon="clipboard" class="header-icon" />
                Visitor Information
              </h5>
            </template>

            <b-card-body>
              <b-form @submit.prevent="submitCheckin">
                <!-- Visitor Type -->
                <div class="form-section">
                  <label class="form-label">Visitor Type</label>
                  <b-button-group class="visitor-type-group">
                    <b-button
                      variant="outline-primary"
                      :pressed="visitorType === 'new'"
                      @click="visitorType = 'new'"
                      class="type-btn"
                    >
                      <b-icon icon="person-plus" />
                      New
                    </b-button>
                    <b-button
                      variant="outline-success"
                      :pressed="visitorType === 'returning'"
                      @click="visitorType = 'returning'"
                      class="type-btn"
                    >
                      <b-icon icon="person-check" />
                      Returning
                    </b-button>
                    <b-button
                      variant="outline-warning"
                      :pressed="visitorType === 'employee'"
                      @click="visitorType = 'employee'"
                      class="type-btn"
                    >
                      <b-icon icon="person-badge" />
                      Employee
                    </b-button>
                  </b-button-group>
                </div>

                <!-- Search -->
                <div class="form-section" v-if="visitorType === 'returning'">
                  <label class="form-label">Search Name / Phone</label>
                  <b-input-group>
                    <b-form-input
                      v-model="searchQuery"
                      placeholder="Type name or phone..."
                      @input="searchVisitors"
                      class="search-input"
                    />
                    <b-input-group-append>
                      <b-button variant="primary" @click="searchVisitors" class="search-btn">
                        <b-icon icon="search" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>

                  <b-list-group v-if="searchResults.length > 0" class="mt-2 search-results glass-list">
                    <b-list-group-item
                      v-for="visitor in searchResults"
                      :key="visitor.id"
                      @click="selectVisitor(visitor)"
                      button
                      class="search-result-item"
                    >
                      <div class="result-name">{{ visitor.name }}</div>
                      <div class="result-details">
                        <span>Phone: {{ visitor.phone || 'N/A' }}</span>
                        <span class="result-visits">Visits: {{ visitor.visits_count }}</span>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </div>

           <b-row class="form-section">
  <b-col md="3" sm="6" xs="12">
    <label class="form-label">Visitor Name</label>
    <b-form-input
      v-model="visitorName"
      placeholder="Enter visitor name"
      :required="visitorType === 'new'"
      class="form-input"
    />
  </b-col>
  
  <b-col md="3" sm="6" xs="12">
    <label class="form-label">Phone Number</label>
    <b-form-input
      v-model="phone"
      placeholder="Enter phone number"
      class="form-input"
    />
  </b-col>
  
  <b-col md="3" sm="6" xs="12">
    <label class="form-label">Notes</label>
    <b-form-input
      v-model="notes"
      placeholder="Optional notes..."
      class="form-input"
    />
  </b-col>
  
  <b-col md="3" sm="6" xs="12">
    <label class="form-label">Checked In By</label>
    <b-form-select
      v-model="selectedEmployee"
      :options="employeeOptions"
      required
      class="form-select"
    />
  </b-col>
</b-row>
                <!-- Submit -->
                <b-button
                  type="submit"
                  variant="primary"
                  block
                  class="submit-btn"
                  :disabled="isSubmitting || isVerifying"
                >
                  <b-spinner v-if="isSubmitting" small class="mr-2" />
                  <b-icon v-else icon="check-circle" class="mr-2" />
                  {{ isSubmitting ? 'Submitting...' : 'Submit Check-In' }}
                </b-button>
              </b-form>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Right: Camera -->
        <b-col lg="6" md="12">
          <b-card class="camera-card glass-card" no-body>
            <template #header>
              <div class="camera-header">
                <h5 class="card-header-title">
                  <b-icon icon="camera" class="header-icon" />
                  Face Capture
                </h5>
                <b-badge v-if="useRealCamera && cameraReady" variant="success" class="camera-status-badge">
                  <b-icon icon="camera-video" /> Live
                </b-badge>
                <b-badge v-else-if="useRealCamera && !cameraReady" variant="warning" class="camera-status-badge">
                  <b-icon icon="exclamation-triangle" /> Connecting...
                </b-badge>
                <b-badge v-else-if="!useRealCamera && isVerifying" variant="info" class="camera-status-badge">
                  <b-icon icon="image" /> Generated
                </b-badge>
              </div>
            </template>

            <b-card-body class="camera-body">
              <!-- Hidden elements -->
              <video ref="videoElement" autoplay playsinline muted style="display: none;"></video>
              <canvas ref="canvasElement" style="display: none;"></canvas>

              <!-- Camera Content -->
              <div class="camera-content">
                <!-- Idle State -->
                <div v-if="!isVerifying && !isSubmitting" class="camera-idle">
                  <b-icon icon="camera" scale="4" class="idle-icon" />
                  <p class="idle-text">Submit check-in to start face capture</p>
                </div>

                <!-- Active State -->
                <div v-else class="camera-active">
                  <!-- Preview -->
                  <div class="camera-preview glass-preview">
                    <img
                      v-if="currentFrame"
                      :src="currentFrame"
                      alt="Camera feed"
                      class="camera-frame"
                    />
                    <div v-else class="camera-loading">
                      <b-spinner variant="primary" />
                      <p class="mt-2">Initializing camera...</p>
                    </div>
                  </div>

                  <!-- Progress -->
                  <div class="camera-progress">
                    <b-progress
                      :value="captureProgress"
                      max="100"
                      variant="primary"
                      animated
                      class="progress-bar-custom"
                    />
                    <div class="progress-label">
                      <span class="progress-text">{{ captureStatus }}</span>
                      <span class="progress-percent">{{ Math.round(captureProgress) }}%</span>
                    </div>
                  </div>

                  <!-- Result -->
                  <div v-if="verificationResult" class="verification-result">
                    <b-alert
                      :variant="getAlertVariant(verificationResult.status)"
                      show
                      class="result-alert glass-alert"
                    >
                      <div class="result-header">
                        <b-icon
                          :icon="getResultIcon(verificationResult.status)"
                          class="result-icon"
                        />
                        <strong>{{ verificationResult.status }}</strong>
                      </div>
                      <div class="result-message">{{ verificationResult.message }}</div>

                      <!-- Duplicate Details -->
                      <div v-if="verificationResult.status === 'FLAG_DUPLICATE'" class="result-details">
                        <b-badge variant="danger" class="duplicate-badge">
                          <b-icon icon="exclamation-triangle" /> Duplicate Check-in!
                        </b-badge>
                        <div v-if="verificationResult.duplicate_details" class="duplicate-info">
                          Already checked in {{ verificationResult.duplicate_details.count }} time(s) today
                          <br v-if="verificationResult.duplicate_details.first_checkin_time">
                          First check-in at: {{ formatTime(verificationResult.duplicate_details.first_checkin_time) }}
                        </div>
                      </div>

                      <!-- Visitor Type -->
                      <div v-if="verificationResult.visitor_type" class="result-visitor">
                        <b-badge :variant="getTypeBadgeVariant(verificationResult.visitor_type)">
                          {{ verificationResult.visitor_type }}
                        </b-badge>
                        <span v-if="verificationResult.visitor_name" class="visitor-name">
                          {{ verificationResult.visitor_name }}
                        </span>
                        <span v-if="verificationResult.visits_count" class="visitor-count">
                          Visit #{{ verificationResult.visits_count }}
                        </span>
                      </div>
                    </b-alert>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CheckinScreen',
  data() {
    return {
      visitorType: 'new',
      searchQuery: '',
      searchResults: [],
      visitorName: '',
      phone: '',
      notes: '',
      selectedEmployee: null,
      employeeOptions: [{ value: null, text: 'Select employee...', disabled: true }],

      isSubmitting: false,
      isVerifying: false,
      checkinId: null,

      currentFrame: null,
      captureProgress: 0,
      captureStatus: '',
      verificationResult: null,

      captureInterval: null,
      captureCount: 0,
      maxCaptures: 20,

      useRealCamera: false,
      stream: null,
      cameraReady: false,
    };
  },

  created() {
    this.loadEmployees();
  },

  beforeDestroy() {
    this.stopCapture();
    this.stopWebcam();
  },

  methods: {
    async loadEmployees() {
      try {
        const response = await axios.get('/api/visitor/employees/list/', {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        this.employeeOptions = [{ value: null, text: 'Select employee...', disabled: true }];
        response.data.forEach(emp => {
          this.employeeOptions.push({
            value: emp.id,
            text: `${emp.name} ${emp.has_face ? '✓' : '⚠️ No Face'}`,
            disabled: !emp.has_face
          });
        });
      } catch (error) {
        console.error('Failed to load employees', error);
        this.$bvToast.toast('Failed to load employees', { variant: 'danger' });
      }
    },

    async searchVisitors() {
      if (this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }
      try {
        const response = await axios.get('/api/visitor/visitors/search/', {
          params: { q: this.searchQuery },
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });
        this.searchResults = response.data;
      } catch (error) {
        console.error('Search failed', error);
      }
    },

    selectVisitor(visitor) {
      this.visitorName = visitor.name;
      this.phone = visitor.phone;
      this.searchQuery = visitor.name;
      this.searchResults = [];
    },

    async submitCheckin() {
      if (!this.selectedEmployee) {
        this.$bvToast.toast('Please select an employee', { variant: 'warning' });
        return;
      }
      if (this.visitorType === 'new' && !this.visitorName) {
        this.$bvToast.toast('Please enter visitor name', { variant: 'warning' });
        return;
      }

      this.isSubmitting = true;
      this.verificationResult = null;

      try {
        const payload = {
          visitor_name: this.visitorName || this.searchQuery,
          phone: this.phone,
          notes: this.notes
        };

        const response = await axios.post('/api/visitor/checkin/', payload, {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        this.checkinId = response.data.checkin_id;
        this.isSubmitting = false;
        this.isVerifying = true;
        await this.startFaceCapture();
        this.$bvToast.toast('Check-in submitted, capturing face...', { variant: 'info' });
      } catch (error) {
        console.error('Check-in failed', error);
        this.isSubmitting = false;
        this.$bvToast.toast('Check-in failed: ' + (error.response.data.error || 'Unknown error'), { variant: 'danger' });
      }
    },

    async startFaceCapture() {
      this.captureCount = 0;
      this.captureProgress = 0;
      this.captureStatus = 'Initializing camera...';

      try {
        await this.initWebcam();
        this.useRealCamera = true;
        this.captureStatus = 'Camera ready, capturing frames...';
        this.startCaptureLoop();
      } catch (error) {
        console.warn('Webcam not available, using generated images:', error);
        this.useRealCamera = false;
        this.captureStatus = 'Using generated images (no camera found)';
        this.$bvToast.toast('Webcam not found. Using generated images for testing.', { variant: 'warning' });
        this.startGeneratedCapture();
      }
    },

    async initWebcam() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: false
        });

        const video = this.$refs.videoElement;
        video.srcObject = this.stream;
        await new Promise((resolve) => { video.onloadedmetadata = resolve; });
        await video.play();

        const canvas = this.$refs.canvasElement;
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        this.cameraReady = true;
        return true;
      } catch (error) {
        console.error('Webcam initialization failed:', error);
        throw error;
      }
    },

    startCaptureLoop() {
      if (this.captureInterval) clearInterval(this.captureInterval);

      this.captureInterval = setInterval(() => {
        this.captureCount++;
        this.captureProgress = (this.captureCount / this.maxCaptures) * 100;

        if (this.useRealCamera && this.cameraReady) {
          this.captureFrameFromWebcam();
        } else {
          this.currentFrame = this.generateRealImage();
        }

        if (this.captureCount % 5 === 0) {
          this.verifyFace();
        }

        if (this.captureCount >= this.maxCaptures) {
          this.stopCapture();
          if (!this.verificationResult) {
            this.verificationResult = { status: 'TIMEOUT', message: 'Face capture timeout' };
          }
        }
      }, 500);
    },

    captureFrameFromWebcam() {
      try {
        const video = this.$refs.videoElement;
        const canvas = this.$refs.canvasElement;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        this.currentFrame = canvas.toDataURL('image/jpeg', 0.8);
        this.captureStatus = `Captured frame ${this.captureCount}/${this.maxCaptures}`;
      } catch (error) {
        console.error('Error capturing frame:', error);
        this.currentFrame = this.generateRealImage();
        this.captureStatus = 'Using fallback image';
      }
    },

    startGeneratedCapture() {
      this.currentFrame = this.generateRealImage();
      if (this.captureInterval) clearInterval(this.captureInterval);

      this.captureInterval = setInterval(() => {
        this.captureCount++;
        this.captureProgress = (this.captureCount / this.maxCaptures) * 100;
        this.currentFrame = this.generateRealImage();
        this.captureStatus = `Generated frame ${this.captureCount}/${this.maxCaptures}`;

        if (this.captureCount % 5 === 0) this.verifyFace();
        if (this.captureCount >= this.maxCaptures) {
          this.stopCapture();
          if (!this.verificationResult) {
            this.verificationResult = { status: 'TIMEOUT', message: 'Face capture timeout' };
          }
        }
      }, 500);
    },

    generateRealImage() {
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext('2d');

      // Background
      const gradient = ctx.createLinearGradient(0, 0, 640, 480);
      gradient.addColorStop(0, '#e8e8e8');
      gradient.addColorStop(1, '#d0d0d0');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 640, 480);

      // Face
      ctx.beginPath();
      ctx.arc(320, 200, 100, 0, 2 * Math.PI);
      ctx.fillStyle = '#cccccc';
      ctx.fill();
      ctx.strokeStyle = '#aaaaaa';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Eyes
      ctx.beginPath();
      ctx.arc(280, 175, 15, 0, 2 * Math.PI);
      ctx.fillStyle = '#999999';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(360, 175, 15, 0, 2 * Math.PI);
      ctx.fillStyle = '#999999';
      ctx.fill();

      // Pupils
      ctx.beginPath();
      ctx.arc(285, 175, 5, 0, 2 * Math.PI);
      ctx.fillStyle = '#333333';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(365, 175, 5, 0, 2 * Math.PI);
      ctx.fillStyle = '#333333';
      ctx.fill();

      // Mouth
      ctx.beginPath();
      ctx.arc(320, 235, 50, 0, Math.PI);
      ctx.strokeStyle = '#999999';
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.fillStyle = '#666666';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`📸 Capturing... ${this.captureCount}/${this.maxCaptures}`, 320, 380);

      return canvas.toDataURL('image/jpeg', 0.8);
    },

    stopCapture() {
      if (this.captureInterval) {
        clearInterval(this.captureInterval);
        this.captureInterval = null;
      }
      this.isVerifying = false;
      this.captureStatus = 'Capture complete';
      this.stopWebcam();
    },

    stopWebcam() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
        this.cameraReady = false;
      }
    },

    async verifyFace() {
      if (!this.currentFrame) return;

      try {
        const response = await axios.post('/api/visitor/verify_face/', {
          checkin_id: this.checkinId,
          frame: this.currentFrame
        });

        this.verificationResult = response.data;

        if (response.data.status === 'VERIFIED' ||
            response.data.status === 'EMPLOYEE' ||
            response.data.status === 'FLAG_DUPLICATE' ||
            response.data.status === 'FLAG_NO_CAMERA') {
          this.stopCapture();

          if (response.data.status === 'VERIFIED') {
            this.$bvToast.toast('✅ Check-in verified!', { variant: 'success' });
          } else if (response.data.status === 'EMPLOYEE') {
            this.$bvToast.toast(`👤 Employee ${response.data.employee_name} verified`, { variant: 'success' });
          } else if (response.data.status === 'FLAG_DUPLICATE') {
            this.$bvToast.toast('⚠️ Duplicate check-in detected!', { variant: 'danger' });
          } else {
            this.$bvToast.toast('⚠️ ' + response.data.message, { variant: 'warning' });
          }
        }
      } catch (error) {
        console.error('Verification failed:', error);
      }
    },

    getAlertVariant(status) {
      if (status === 'VERIFIED' || status === 'EMPLOYEE') return 'success';
      if (status === 'FLAG_DUPLICATE') return 'danger';
      if (status === 'FLAG_NO_CAMERA') return 'warning';
      return 'info';
    },

    getResultIcon(status) {
      if (status === 'VERIFIED' || status === 'EMPLOYEE') return 'check-circle-fill';
      if (status === 'FLAG_DUPLICATE') return 'exclamation-triangle-fill';
      if (status === 'FLAG_NO_CAMERA') return 'camera';
      return 'info-circle';
    },

    getTypeBadgeVariant(type) {
      const variants = {
        'NEW': 'success',
        'RETURNING': 'primary',
        'EMPLOYEE': 'warning',
        'DUPLICATE': 'danger'
      };
      return variants[type] || 'secondary';
    },

    formatTime(isoString) {
      if (!isoString) return 'N/A';
      const date = new Date(isoString);
      return date.toLocaleTimeString();
    },

    resetForm() {
      this.visitorType = 'new';
      this.searchQuery = '';
      this.visitorName = '';
      this.phone = '';
      this.notes = '';
      this.selectedEmployee = null;
      this.checkinId = null;
      this.currentFrame = null;
      this.captureProgress = 0;
      this.captureStatus = '';
      this.verificationResult = null;
      this.isSubmitting = false;
      this.isVerifying = false;
      this.useRealCamera = false;
      this.stopWebcam();
    }
  }
};
</script>

<style scoped>
/* ============================================
   BACKGROUND IMAGE (blurred, transparent)
   ============================================ */
.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(8px);
  opacity: 0.7;
  z-index: -1;
  transform: scale(1.1);
  transition: filter 0.3s ease;
}

/* ============================================
   PAGE – glass container
   ============================================ */
.visitor-checkin {
  padding: 24px;
  min-height: 100vh;
  position: relative;
}

/* ============================================
   PAGE HEADER
   ============================================ */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-weight: 700;
  color: #0b1a30;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  text-shadow: 0 1px 4px rgba(255, 255, 255, 0.4);
}

.title-icon {
  color: #4a6cf7;
}

.page-subtitle {
  color: #1f2a3e;
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.5);
}

/* ============================================
   GLASS CARDS
   ============================================ */
.glass-card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.25);
  overflow: hidden;
  height: 100%;
}

.glass-card >>> .card-header {
  background: rgba(244, 247, 252, 0.5);
  backdrop-filter: blur(4px);
  border-bottom: 1px solid rgba(220, 226, 236, 0.3);
  padding: 14px 24px;
}

.glass-card >>> .card-body {
  padding: 24px;
}

.card-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #0b1a30;
  margin: 0;
  font-size: 17px;
}

.header-icon {
  color: #4a6cf7;
}

/* ============================================
   FORM
   ============================================ */
.form-section {
  margin-bottom: 20px;
}

.form-section:last-of-type {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2a3e;
  margin-bottom: 6px;
}

/* Visitor Type Buttons */
.visitor-type-group {
  display: flex;
  width: 100%;
}

.type-btn {
  flex: 1;
  font-size: 14px;
  padding: 8px 12px;
  border-color: rgba(74, 108, 247, 0.3);
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(2px);
  transition: all 0.2s ease;
}

.type-btn:not(:last-child) {
  border-radius: 6px 0 0 6px;
}
.type-btn:last-child {
  border-radius: 0 6px 6px 0;
}

.type-btn.active,
.type-btn.pressed {
  background: rgba(74, 108, 247, 0.2) !important;
  border-color: #4a6cf7 !important;
  box-shadow: 0 0 0 2px rgba(74, 108, 247, 0.15);
}

.type-btn:hover {
  background: rgba(74, 108, 247, 0.1);
}

/* Form Inputs – glass style */
.form-input,
.search-input,
.form-textarea,
.form-select {
  border: 1px solid rgba(208, 213, 221, 0.5);
  border-radius: 10px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
  color: #0b1a30;
}

.form-input:focus,
.search-input:focus,
.form-textarea:focus,
.form-select:focus {
  border-color: #4a6cf7;
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.15);
  background: rgba(255, 255, 255, 0.7);
}

.search-btn {
  background: rgba(74, 108, 247, 0.8);
  border-color: rgba(74, 108, 247, 0.8);
  backdrop-filter: blur(4px);
}
.search-btn:hover {
  background: #3a5cd9;
  border-color: #3a5cd9;
}

/* Search Results – glass */
.search-results.glass-list {
  max-height: 200px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid rgba(226, 230, 238, 0.5);
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(6px);
}

.search-result-item {
  cursor: pointer;
  border: none !important;
  border-bottom: 1px solid rgba(240, 242, 245, 0.5) !important;
  padding: 10px 14px;
  background: transparent !important;
  transition: background 0.15s ease;
}

.search-result-item:hover {
  background: rgba(245, 248, 255, 0.6) !important;
}

.result-name {
  font-weight: 600;
  color: #0b1a30;
}

.result-details {
  font-size: 12px;
  color: #3e5670;
  display: flex;
  gap: 16px;
  margin-top: 2px;
}

.result-visits {
  color: #4a6cf7;
  font-weight: 600;
}

/* Submit Button */
.submit-btn {
  width: 30%;;
  font-weight: 500;
  font-size: 14px;
  border-radius: 10px;
  background: #4a6cf7;
  border-color: #4a6cf7;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(74, 108, 247, 0.25);
}

.submit-btn:hover:not(:disabled) {
  background: #3a5cd9;
  border-color: #3a5cd9;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(74, 108, 247, 0.35);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============================================
   CAMERA CARD
   ============================================ */
.camera-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.camera-status-badge {
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.camera-body {
  padding: 24px;
}

.camera-content {
  min-height: 320px;
}

/* Idle State */
.camera-idle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.idle-icon {
  color: rgba(208, 213, 221, 0.7);
}

.idle-text {
  color: #3e5670;
  margin-top: 12px;
  font-weight: 500;
}

/* Active State */
.camera-active {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Preview – glass */
.camera-preview.glass-preview {
  background: rgba(248, 249, 252, 0.5);
  backdrop-filter: blur(4px);
  border-radius: 12px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(226, 230, 238, 0.4);
}

.camera-frame {
  max-height: 300px;
  max-width: 100%;
  object-fit: contain;
}

.camera-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #3e5670;
}

/* Progress */
.camera-progress {
  margin-top: 4px;
}

.progress-bar-custom {
  height: 8px;
  border-radius: 6px;
  background: rgba(226, 230, 238, 0.6);
}

.progress-bar-custom >>> .progress-bar {
  background: #4a6cf7;
  border-radius: 6px;
  transition: width 0.3s ease;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
}

.progress-text {
  font-size: 13px;
  color: #1f2a3e;
  font-weight: 500;
}

.progress-percent {
  font-size: 13px;
  font-weight: 700;
  color: #4a6cf7;
}

/* Verification Result – glass alert */
.verification-result {
  margin-top: 4px;
}

.result-alert.glass-alert {
  border-radius: 10px;
  padding: 14px 18px;
  margin: 0;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.result-alert.glass-alert >>> .alert {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
}

.result-icon {
  font-size: 20px;
}

.result-message {
  margin-top: 4px;
  font-size: 14px;
}

.result-details {
  margin-top: 8px;
}

.duplicate-badge {
  font-size: 13px;
  padding: 4px 14px;
}

.duplicate-info {
  margin-top: 4px;
  font-size: 13px;
  color: #3e5670;
}

.result-visitor {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.visitor-name {
  font-weight: 600;
  color: #0b1a30;
}

.visitor-count {
  font-size: 13px;
  color: #3e5670;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 992px) {
  .form-card,
  .camera-card {
    margin-bottom: 20px;
  }
}

@media (max-width: 576px) {
  .visitor-checkin {
    padding: 12px;
  }

  .page-title {
    font-size: 20px;
  }

  .visitor-type-group {
    flex-direction: column;
  }

  .type-btn {
    border-radius: 8px !important;
    margin-bottom: 4px;
  }

  .glass-card >>> .card-body {
    padding: 16px;
  }

  .camera-body {
    padding: 16px;
  }

  .camera-preview.glass-preview {
    min-height: 150px;
  }

  .camera-frame {
    max-height: 200px;
  }

  .submit-btn {
    font-size: 14px;
    padding: 10px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Visitor/CheckinScreen.vue